import PropTypes from 'prop-types';
import { Box, Button, Link, Switch, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { formatDate } from 'utils/utils';
import {
	COMPARISON_OPTIONS,
	SEVERITY_COLOR_MAP,
	TENANT_ADMIN_VISIBILITY_OPTIONS,
	WEEKDAY_OPTIONS,
} from 'features/IRWorkstation/utils/alertConstants';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// --------------------------------------------------------------------------------------------------------------------------------
// Column 1: Alert
// Main Component: <AlertSubscriptionCell/>
// Sub Components: <AlertRuleMessage/> <AlertRuleDetails/> <ExpandRuleButton/> <ViewReportInCrmLink/> <AlertRuleMetric/>
// Expanded Content: when user click on <ExpandRuleButton/>, <AlertRuleMetric/> is displayed
// --------------------------------------------------------------------------------------------------------------------------------

const blueTextStyle = {
	fontSize: '11px',
	fontWeight: 600,
	color: 'primary.main',
};

// Column 1: Alert - AlertSubscriptionCell (Main Cell)

export const AlertSubscriptionCell = ({
	params,
	isRuleExpanded,
	onToggleRule,
	onViewReportInCrm,
}) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-start',
			alignSelf: 'flex-start',
			overflow: 'hidden',
			pt: '10px',
			pb: 0,
		}}
	>
		<AlertRuleTitle title={params.row.title} />
		<AlertRuleMessage message={params.row.message} />
		<AlertRuleDetails
			createdAt={params.row.created_at}
			createdByName={params.row.created_by_name}
			subscribedAt={params.row.subscribed_at}
		/>

		{/* Show/Hide Rule Expand Button | View Report in CRM (link) */}

		{(params.row.metric || params.row.report_url) && (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				{params.row.metric && (
					<ExpandRuleButton
						expanded={isRuleExpanded}
						onClick={() => onToggleRule?.(params.row.id, isRuleExpanded)}
					/>
				)}
				{params.row.report_url && (
					<ViewReportInCrmLink
						href={params.row.report_url}
						onClick={onViewReportInCrm}
					/>
				)}
			</Box>
		)}
	</Box>
);

AlertSubscriptionCell.propTypes = {
	params: PropTypes.object.isRequired,
	isRuleExpanded: PropTypes.bool,
	onToggleRule: PropTypes.func,
	onViewReportInCrm: PropTypes.func,
};

// Column 1: Alert - AlertSubscriptionCell - AlertRuleTitle

const AlertRuleTitle = ({ title }) => (
	<Typography
		sx={{
			fontSize: '13px',
			fontWeight: 600,
			color: 'text.primary',
			mb: 1,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		}}
	>
		{title || '(No Subject)'}
	</Typography>
);

AlertRuleTitle.propTypes = {
	title: PropTypes.string,
};

// Column 1: Alert - AlertSubscriptionCell - AlertRuleMessage

const AlertRuleMessage = ({ message }) => (
	<Typography
		sx={{
			fontSize: '12px',
			mb: 2,
		}}
	>
		{message}
	</Typography>
);

AlertRuleMessage.propTypes = {
	message: PropTypes.string,
};

// Column 1: Alert - AlertSubscriptionCell - AlertRuleDetails

const AlertRuleDetails = ({ createdAt, createdByName, subscribedAt }) => (
	<Typography
		sx={{
			fontSize: '11px',
			color: 'text.secondary',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		}}
	>
		Created: {formatDate(createdAt)}
		{createdByName && ` by ${createdByName}`}
		{subscribedAt && ` • Subscribed: ${formatDate(subscribedAt)}`}
	</Typography>
);

AlertRuleDetails.propTypes = {
	createdAt: PropTypes.string,
	createdByName: PropTypes.string,
	subscribedAt: PropTypes.string,
};

// Column 1: Alert - AlertSubscriptionCell - ExpandRuleButton

const ExpandRuleButton = ({ expanded, onClick }) => (
	<Button
		size='small'
		variant='text'
		disableRipple
		onClick={onClick}
		endIcon={
			expanded ? (
				<ArrowDropUpIcon fontSize='large' sx={{ transform: 'scaleX(0.7)' }} />
			) : (
				<ArrowDropDownIcon fontSize='large' sx={{ transform: 'scaleX(0.7)' }} />
			)
		}
		sx={{
			...blueTextStyle,
			ml: '-4px',
			pl: 1,
			py: '2px',
			'&:hover': {
				backgroundColor: 'transparent',
			},
			'& .MuiButton-endIcon': {
				mx: '-4px',
				'& > *:first-of-type': {
					fontSize: '26px',
				},
			},
		}}
	>
		{expanded ? 'Hide Rule & Cadence' : 'Show Rule & Cadence'}
	</Button>
);

ExpandRuleButton.propTypes = {
	expanded: PropTypes.bool,
	onClick: PropTypes.func,
};

// Column 1: Alert - AlertSubscriptionCell - ViewReportInCrmLink

const ViewReportInCrmLink = ({ href, onClick }) => (
	<Link
		href={href}
		target='_blank'
		rel='noopener noreferrer'
		underline='none'
		onClick={onClick}
		sx={{
			...blueTextStyle,
			'&:hover': { backgroundColor: 'transparent' },
		}}
	>
		View Report in CRM
	</Link>
);

ViewReportInCrmLink.propTypes = {
	href: PropTypes.string.isRequired,
	onClick: PropTypes.func,
};

// -------------------------------------------------------------------------------------------------------------------
// Column 1: Alert - AlertSubscriptionCell - AlertRuleMetric (expanded content when user click on <ExpandRuleButton/>)
// -------------------------------------------------------------------------------------------------------------------

export const formatCadence = (schedule) => {
	if (schedule?.frequency === 'weekly' && schedule?.day_of_week) {
		const dayLabel =
			WEEKDAY_OPTIONS.find(
				(option) =>
					option.value.toLowerCase() === schedule.day_of_week.toLowerCase()
			)?.label ?? schedule.day_of_week;
		return `Cadence: Runs Weekly on ${dayLabel}`;
	}
	return 'Cadence: Runs Nightly';
};

export const AlertRuleMetric = ({ metric, schedule }) => {
	const mainStyle = {
		display: 'flex',
		flexDirection: 'column',
		gap: 0.5,
		px: '15px',
		pb: 2,
	};

	const metricRowStyle = {
		display: 'flex',
		alignItems: 'center',
		gap: 1,
		fontSize: '11px',
		fontWeight: 600,
		color: 'text.secondary',
	};

	const metricKeyStyle = {
		px: 2,
		py: '2px',
		borderRadius: '4px',
		backgroundColor: 'background.paper',
		border: '1px solid',
		borderColor: 'border.light',
		fontWeight: 400,
		color: 'text.primary',
	};

	const comparisonLabel = (
		COMPARISON_OPTIONS.find((option) => option.value === metric?.comparison)
			?.label ?? metric?.comparison
	)?.toLowerCase();

	return (
		<Box sx={mainStyle}>
			<Box sx={metricRowStyle}>
				<Typography variant='inherit'>Alert when</Typography>

				<Box component='span' sx={metricKeyStyle}>
					{metric?.metric_key}
				</Box>

				<Typography variant='inherit'>is</Typography>

				<Typography component='span' variant='inherit'>
					{comparisonLabel}
				</Typography>

				<Typography
					component='span'
					variant='inherit'
					sx={{ fontWeight: 700, color: 'text.primary' }}
				>
					{metric?.threshold}
				</Typography>
			</Box>

			<Typography
				sx={{
					fontSize: '11px',
					fontWeight: 600,
					color: 'text.secondary',
				}}
			>
				{formatCadence(schedule)}
			</Typography>
		</Box>
	);
};

AlertRuleMetric.propTypes = {
	metric: PropTypes.shape({
		metric_key: PropTypes.string,
		comparison: PropTypes.string,
		threshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}),
	schedule: PropTypes.shape({
		frequency: PropTypes.string,
		day_of_week: PropTypes.string,
	}),
};

// -------------------------------------------------------------------------
// Column 2: Severity
// -------------------------------------------------------------------------

export const SeverityCell = ({ params }) => {
	const severity = (params.row.severity ?? 'info').toLowerCase();

	return (
		<Typography
			sx={{
				fontSize: '12px',
				fontWeight: 600,
				color: SEVERITY_COLOR_MAP[severity] ?? SEVERITY_COLOR_MAP['info'],
				textTransform: 'capitalize',
			}}
		>
			{params.row.severity}
		</Typography>
	);
};

SeverityCell.propTypes = {
	params: PropTypes.object.isRequired,
};

// -------------------------------------------------------------------------
// Column 3: Visibility
// -------------------------------------------------------------------------

export const VisibilityCell = ({ params }) => {
	const visibility =
		TENANT_ADMIN_VISIBILITY_OPTIONS.find(
			(option) => option.value === params.row.visibility
		)?.label ?? params.row.visibility;

	return (
		<Typography
			sx={{
				fontSize: '12px',
				fontWeight: 600,
				color: 'text.secondary',
				textTransform: 'capitalize',
			}}
		>
			{visibility}
		</Typography>
	);
};

VisibilityCell.propTypes = {
	params: PropTypes.object.isRequired,
};

// -------------------------------------------------------------------------
// Column 4: Subscriptions
// -------------------------------------------------------------------------

export const SubscriptionsCell = ({ params }) => {
	return (
		<Typography
			sx={{
				fontSize: '12px',
				fontWeight: 600,
				color: 'text.secondary',
			}}
		>
			{params.row.subscription_count}
		</Typography>
	);
};

SubscriptionsCell.propTypes = {
	params: PropTypes.object.isRequired,
};

// -------------------------------------------------------------------------
// Column 5: Subscribed
// -------------------------------------------------------------------------

export const SubscribedCell = ({
	params,
	handleSubscribe,
	handleUnsubscribe,
}) => {
	const alert = params.row;

	return (
		<Switch
			checked={!!alert.subscribed}
			onChange={() =>
				alert.subscribed ? handleUnsubscribe?.(alert) : handleSubscribe?.(alert)
			}
			size='small'
			slotProps={{
				input: {
					'aria-label': `${alert.subscribed ? 'Unsubscribe from' : 'Subscribe to'} ${alert.title}`,
				},
			}}
		/>
	);
};

SubscribedCell.propTypes = {
	params: PropTypes.object.isRequired,
	handleSubscribe: PropTypes.func,
	handleUnsubscribe: PropTypes.func,
};

// -------------------------------------------------------------------------
// Column 6: Actions
// -------------------------------------------------------------------------

export const ActionsCell = ({ params, handleRunNow, handleDeleteRule }) => {
	const alert = params.row;
	const { tenant } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	const handleRunNowAction = (alert) => {
		handleRunNow?.(alert)
		trackButtonClick(ButtonClickTargets.AlertCenterRunRuleAction);
	};

	const actions = [
		<GridActionsCellItem
			key={`run-rule-now-${alert.id}`}
			label='Run Now'
			showInMenu
			onClick={() => handleRunNowAction?.(alert)}
		/>,
	];

	if (alert.is_owner) {
		actions.push(
			<GridActionsCellItem
				key={`delete-rule-${alert.id}`}
				label='Delete'
				showInMenu
				onClick={() => handleDeleteRule?.(alert)}
			/>
		);
	}

	return actions;
};

ActionsCell.propTypes = {
	params: PropTypes.object.isRequired,
	handleRunNow: PropTypes.func,
	handleDeleteRule: PropTypes.func,
};
